import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import client from "../../api/client";
import { downloadSubmissionFile } from "../../utils/submissionFile";
import SubmissionFilePreview from "./SubmissionFilePreview";

const GRADE_COLOR = {
  A: "bg-green-100 text-green-700",
  B: "bg-blue-100 text-blue-700",
  C: "bg-yellow-100 text-yellow-700",
  D: "bg-orange-100 text-orange-700",
  F: "bg-red-100 text-red-700",
};

const ACCEPTED_FILES =
  ".pdf,.zip,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.md,.json,.ipynb,.py,.jpg,.jpeg,.png,.gif,.webp,.mp4";

const HONEYPOT_CLASS = "absolute -left-[9999px] w-px h-px opacity-0 overflow-hidden";

const graceActive = (project) =>
  project?.grace_until && new Date(project.grace_until) > new Date();

function SubmitModal({ project, submission, onClose, onSaved }) {
  const [mode, setMode] = useState(submission?.file_path ? "file" : "link");
  const [form, setForm] = useState({
    title: submission?.title || "",
    description: submission?.description || "",
    file_link: submission?.file_link || "",
    image: submission?.image || "",
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const startedAt = useRef(Date.now());
  const [honeypot, setHoneypot] = useState("");

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const canSubmit =
    form.title.trim() &&
    form.description.trim() &&
    (mode === "file" ? !!file || !!submission?.file_path : form.file_link.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error(mode === "file" ? "Choose a file to upload." : "Add a link to your work.");
      return;
    }
    setSaving(true);
    try {
      let res;
      if (mode === "file" && file) {
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("description", form.description);
        if (form.image) fd.append("image", form.image);
        fd.append("file", file);
        fd.append("website", honeypot);
        fd.append("form_started_at", String(startedAt.current));
        res = await client.post(`/projects/${project.id}/submit`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await client.post(`/projects/${project.id}/submit`, {
          title: form.title,
          description: form.description,
          image: form.image || undefined,
          file_link: mode === "link" ? form.file_link.trim() : undefined,
          website: honeypot,
          form_started_at: startedAt.current,
        });
      }
      toast.success("Submission saved!");
      onSaved(res.data.submission);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save submission.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-800">
            {submission ? "Resubmit" : "Submit"} — {project.name}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className={HONEYPOT_CLASS}
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              name="title"
              value={form.title}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40"
              placeholder="Give your submission a title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              required
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40 resize-none"
              placeholder="Describe your work…"
            />
          </div>

          <div>
            <div className="flex gap-2 mb-2">
              {[
                { key: "file", label: "Upload File" },
                { key: "link", label: "Paste Link" },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setMode(t.key)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                    mode === t.key
                      ? "bg-[#F05A19] text-white border-[#F05A19]"
                      : "border-gray-300 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {mode === "file" ? (
              <>
                <input
                  type="file"
                  accept={ACCEPTED_FILES}
                  onChange={(e) => setFile(e.target.files[0] || null)}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[#F05A19]/10 file:text-[#F05A19] hover:file:bg-[#F05A19]/20 cursor-pointer"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Max 10 MB. Notebooks (.ipynb) and Python (.py) accepted.
                  {submission?.file_path && !file && " Leave empty to keep your current file."}
                </p>
              </>
            ) : (
              <input
                name="file_link"
                value={form.file_link}
                onChange={handleChange}
                type="url"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F05A19]/40"
                placeholder="https://github.com/you/project"
              />
            )}
          </div>

          {graceActive(project) && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
              Grace period — submit by {new Date(project.grace_until).toLocaleString()}.
            </p>
          )}

          {submission && (
            <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
              This replaces your previous submission — the instructor will review your latest work.
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 rounded-lg py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !canSubmit}
              className="flex-1 bg-[#F05A19] hover:bg-[#d14d14] text-white rounded-lg py-2 text-sm font-medium transition-colors disabled:opacity-60"
            >
              {saving ? "Saving…" : submission ? "Resubmit" : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProjectCard({ project }) {
  const [submission, setSubmission] = useState(null);
  const [loadingSubmission, setLoadingSubmission] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showFilePreview, setShowFilePreview] = useState(false);

  const closed = project.is_open_for_submission === false;
  const inGrace = graceActive(project);

  useEffect(() => {
    (async () => {
      try {
        const res = await client.get(`/projects/${project.id}/submission`);
        setSubmission(res.data.submission);
      } catch {
        // not submitted yet — null is fine
      } finally {
        setLoadingSubmission(false);
      }
    })();
  }, [project.id]);

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-800 text-sm truncate">{project.name}</h3>
            {project.due_at && (
              <p className="text-xs text-gray-400 mt-0.5">
                Due {new Date(project.due_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </p>
            )}
            {inGrace && (
              <p className="text-xs text-amber-600 mt-0.5">
                Grace period until {new Date(project.grace_until).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </p>
            )}
          </div>

          {/* Grade badge */}
          {submission?.grade && (
            <span className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-bold ${GRADE_COLOR[submission.grade]}`}>
              Grade: {submission.grade}
            </span>
          )}
        </div>

        {/* Description toggle */}
        {project.description && (
          <div className="px-5 pb-3">
            <p className={`text-xs text-gray-500 ${expanded ? "" : "line-clamp-2"}`}>{project.description}</p>
            {project.description.length > 120 && (
              <button onClick={() => setExpanded(e => !e)} className="text-xs text-[#F05A19] mt-1">
                {expanded ? "Show less" : "Read more"}
              </button>
            )}
          </div>
        )}

        {/* Submission status */}
        <div className="px-5 pb-4">
          {loadingSubmission ? (
            <div className="h-8 bg-gray-100 rounded animate-pulse w-32" />
          ) : submission ? (
            <div className="space-y-2">
              {/* Current submission info */}
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-green-400" />
                <span className="text-xs text-gray-600">
                  Submitted {new Date(submission.created_at).toLocaleDateString()}
                </span>
                {submission.file_download_url && (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowFilePreview((v) => !v)}
                      className="text-xs text-[#294a93] underline"
                    >
                      {showFilePreview ? "Hide preview" : "Preview file"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        downloadSubmissionFile(submission).catch(() =>
                          toast.error("Could not download this file.")
                        )
                      }
                      className="text-xs text-[#294a93] underline"
                    >
                      Download file
                    </button>
                  </>
                )}
                {submission.file_link && (
                  <a
                    href={submission.file_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#294a93] underline"
                  >
                    View link
                  </a>
                )}
              </div>

              {showFilePreview && submission.file_download_url && (
                <div className="pt-1">
                  <SubmissionFilePreview submission={submission} />
                </div>
              )}

              {/* Instructor feedback */}
              {submission.instructor_feedback && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <p className="text-xs font-semibold text-blue-700 mb-1">Instructor Feedback</p>
                  <p className="text-xs text-blue-900 whitespace-pre-wrap">{submission.instructor_feedback}</p>
                  {submission.feedback_at && (
                    <p className="text-xs text-blue-400 mt-1">
                      {new Date(submission.feedback_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}

              {submission.grade ? (
                <p className="text-xs text-gray-400">
                  Graded — submission locked.
                </p>
              ) : closed ? (
                <p className="text-xs text-gray-400">Submission closed.</p>
              ) : (
                <button
                  onClick={() => setShowModal(true)}
                  className="text-xs text-[#F05A19] font-medium hover:underline"
                >
                  Resubmit
                </button>
              )}
            </div>
          ) : closed ? (
            <span className="inline-block w-full text-center bg-gray-100 text-gray-400 rounded-lg py-2.5 text-sm font-medium">
              Submission closed
            </span>
          ) : (
            <button
              onClick={() => setShowModal(true)}
              className="w-full border-2 border-dashed border-[#F05A19]/40 hover:border-[#F05A19] text-[#F05A19] rounded-lg py-2.5 text-sm font-medium transition-colors"
            >
              + Submit your work
            </button>
          )}
        </div>
      </div>

      {showModal && (
        <SubmitModal
          project={project}
          submission={submission}
          onClose={() => setShowModal(false)}
          onSaved={(updated) => setSubmission(updated)}
        />
      )}
    </>
  );
}

export default function Projects() {
const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await client.get("/projects");
        const raw = res.data;
        setProjects(Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []));
      } catch {
        toast.error("Failed to load projects.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="py-6 px-4 md:px-8 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Projects</h1>
        <p className="text-sm text-gray-500 mt-1">Submit your work and track instructor feedback.</p>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-1/2 mb-4" />
              <div className="h-10 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-4xl mb-3">📋</p>
          <p className="font-medium">No projects yet</p>
          <p className="text-sm mt-1">Projects from your enrolled courses will appear here.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </div>
      )}
    </div>
  );
}
